import React, { useEffect, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import {
    DatasetComponent,
    GridComponent,
    LegendComponent,
    ToolboxComponent,
    TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';
import useToken from 'hooks/useToken';
import { Card } from 'react-bootstrap';

echarts.use([
    DatasetComponent,
    TooltipComponent,
    ToolboxComponent,
    GridComponent,
    LegendComponent,
    LineChart,
    CanvasRenderer,
    UniversalTransition
]);

const SalesLineChart = ({ height }) => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedVendor, setSelectedVendor] = useState('TODOS');
    const [currentYearData, setCurrentYearData] = useState([]);
    const [previousYearData, setPreviousYearData] = useState([]);
    const [vendedores, setVendedores] = useState(['TODOS']);
    const [vendedoresData, setVendedoresData] = useState([]);
    const { token, setToken } = useToken();
    const abortRef = useRef(null);
    const debounceRef = useRef(null);
    const lastRequestedRef = useRef(null);

    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const currentYear = new Date().getFullYear();

    const years = useMemo(() => {
        const yearList = [];
        for (let year = currentYear; year >= 2020; year--) {
            yearList.push(year);
        }
        return yearList;
    }, [currentYear]);

    const getVendorId = (selectedVendor) => {
        if (selectedVendor === 'TODOS') return null;
        const vendedor = vendedoresData.find(v =>
            v.vNombre === selectedVendor || v.vUsuario === selectedVendor
        );
        return vendedor ? vendedor.idUsuario : null;
    };

    const fetchVendedores = async () => {
        try {
            const response = await fetch('/api/Administradores/getVendedores', {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });

            if (response?.ok) {
                const responseType = await response.json();
                if (responseType.iResponseType === 1) {
                    const parsed = JSON.parse(responseType.detalles);
                    const dataArray = parsed.table2;
                    setVendedoresData(dataArray);
                    const vendedoresList = ['TODOS', ...dataArray.map(v => v.vNombre || v.vUsuario)];
                    setVendedores(vendedoresList);
                }
            } else if (response?.status === 401) {
                setToken(null);
                location.href = '';
            }
        } catch (error) {
            console.error('Error en fetchVendedores:', error);
        }
    };

    useEffect(() => {
        fetchVendedores();
    }, []);

    useEffect(() => {
        if (!token) return;
        if (vendedoresData.length === 0) return;
        
        const requestKey = `${selectedYear}-${selectedVendor}`;
        if (lastRequestedRef.current === requestKey) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(() => {
            lastRequestedRef.current = requestKey;

            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            const vendorId = getVendorId(selectedVendor);

            const formData = new URLSearchParams();
            formData.append('anio', selectedYear.toString());
            if (vendorId !== null) {
                formData.append('idUsuario', vendorId.toString());
            }

            fetch('/api/marketing/ObtenerVentaNueva', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': token ? 'Bearer ' + token : ''
                },
                body: formData.toString()
            })
                .then((res) => {
                    if (!res.ok) {
                        if (res.status === 401) {
                            setToken(null);
                            location.href = '';
                        }
                        throw new Error('Error fetching sales');
                    }
                    return res.json();
                })
                .then((response) => {
                    if (response.iResponseType === 1 && response.detalles) {
                        const dataset = JSON.parse(response.detalles);
                        const currentRows = dataset.table2 || [];
                        const previousRows = dataset.table3 || [];
                        
                        const currentMonthlyTotals = new Array(12).fill(0);
                        const previousMonthlyTotals = new Array(12).fill(0);
                        
                        currentRows.forEach(item => {
                            const monthIndex = item.mes - 1;
                            if (monthIndex >= 0 && monthIndex < 12) {
                                currentMonthlyTotals[monthIndex] += item.dTotalMXN || 0;
                            }
                        });

                        previousRows.forEach(item => {
                            const monthIndex = item.mes - 1;
                            if (monthIndex >= 0 && monthIndex < 12) {
                                previousMonthlyTotals[monthIndex] += item.dTotalMXN || 0;
                            }
                        });

                        setCurrentYearData(currentMonthlyTotals);
                        setPreviousYearData(previousMonthlyTotals);
                    } else {
                        setCurrentYearData(new Array(12).fill(0));
                        setPreviousYearData(new Array(12).fill(0));
                    }
                })
                .catch((err) => {
                    if (err.name === 'AbortError') return;
                    setCurrentYearData(new Array(12).fill(0));
                    setPreviousYearData(new Array(12).fill(0));
                });
        }, 250);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (abortRef.current) abortRef.current.abort();
        };
    }, [selectedYear, selectedVendor, token, setToken, vendedoresData]);

    const chartOption = useMemo(() => {
        const previousYear = selectedYear - 1;
        
        return {
            title: {
                text: 'New Sales per Month',
                left: 'left',
                textStyle: { fontSize: 18, fontWeight: 'bold', color: '#374151' }
            },
            tooltip: {
                trigger: 'axis',
                formatter: function (params) {
                    const formatCurrency = (value) => {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(value);
                    };

                    let result = `📅 ${params[0].axisValue}<br/>`;
                    params.forEach(param => {
                        const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>`;
                        result += `${marker}${param.seriesName}: <b>${formatCurrency(param.value)}</b><br/>`;
                    });
                    return result;
                }
            },
            legend: {
                data: [selectedYear.toString(), previousYear.toString()],
                top: 'top',
                right: 'right'
            },
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { 
                type: 'category', 
                boundaryGap: false, 
                data: months 
            },
            yAxis: { 
                type: 'value',
                axisLabel: {
                    formatter: function (value) {
                        return new Intl.NumberFormat('es-MX', {
                            style: 'currency',
                            currency: 'MXN',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            notation: 'compact',
                            compactDisplay: 'short'
                        }).format(value);
                    }
                }
            },
            series: [
                {
                    name: selectedYear.toString(),
                    type: 'line',
                    data: currentYearData,
                    lineStyle: { color: '#3b82f6', width: 3 },
                    itemStyle: { color: '#3b82f6' },
                    symbol: 'circle',
                    symbolSize: 6,
                    smooth: true
                },
                {
                    name: previousYear.toString(),
                    type: 'line',
                    data: previousYearData,
                    lineStyle: { color: '#94a3b8'},
                    itemStyle: { color: '#94a3b8' },
                    symbol: 'circle',
                    symbolSize: 4,
                    smooth: true
                }
            ]
        };
    }, [selectedYear, currentYearData, previousYearData, months]);

    return (
        <Card className="rounded-lg shadow-md p-4 mb-4 w-full">
            <div className="row mb-3">
                <div className="col-md-3 col-6">
                    <div className="form-group">
                        <label className="form-label text-uppercase small fw-medium mb-1">
                            Year
                        </label>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            className="form-select form-select-sm"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="col-md-5 col-6">
                    <div className="form-group">
                        <label className="form-label text-uppercase small fw-medium mb-1">
                            Sales agent
                        </label>
                        <select
                            value={selectedVendor}
                            onChange={(e) => setSelectedVendor(e.target.value)}
                            className="form-select form-select-sm"
                        >
                            {vendedores.map(vendor => (
                                <option key={vendor} value={vendor}>
                                    {vendor === 'TODOS' ? 'TODOS LOS VENDEDORES' : vendor}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <ReactEChartsCore
                echarts={echarts}
                option={chartOption}
                style={{ height: `${height}px`, width: '100%' }}
            />
        </Card>
    );
};

SalesLineChart.propTypes = {
    height: PropTypes.number
};

SalesLineChart.defaultProps = {
    height: 300
};

export default SalesLineChart;
